import { useTheme } from "@mui/material";
import { formatDate } from "date-fns";
import { useAtomValue } from "jotai";
import { EllipsisIcon, FileTextIcon } from "lucide-react";
import { type FC, type MouseEvent, useState } from "react";
import { useIsDocumentTransferPending } from "web_myplann/Api/Document/useIsDocumentTransferPending";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import type { DocumentDTO } from "web_myplann/Infrastructure/Api/Api";
import { DocumentActionsDrawerRedesign } from "web_myplann/Portfolio/Components/Redesign/Documents/DocumentActionsDrawerRedesign";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { RedesignListItemRow } from "web_myplann/Shared/Components/Redesign/RedesignListItemRow";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type Props = {
	item: DocumentDTO;
	activeTag?: string;
};

export const DocumentItemRedesign: FC<Props> = ({ item, activeTag }) => {
	const theme = useTheme();
	const downloadedDocuments = useAtomValue(downloadedDocumentsState);
	const { mutate: openDocument } = useOpenDocumentMutation();
	const isProcessing = useIsDocumentTransferPending();

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const handleOpenDocument = () => {
		if (isProcessing) {
			return;
		}
		openDocument({ id: item.id, fallbackTitle: item.title ?? undefined });
	};

	const handleOpenDrawer = (event: MouseEvent<SVGSVGElement>) => {
		event.stopPropagation();
		if (isProcessing) {
			return;
		}
		setIsDrawerOpen(true);
	};

	return (
		<RedesignListItemRow
			icon={
				<FileTextIcon
					size={theme.iconSize.md}
					color={theme.palette.grey[500]}
				/>
			}
			title={item.title}
			subtitle={formatDate(new Date(item.createdAt ?? ""), DateFormat.Display)}
			onClick={handleOpenDocument}
			rightSlot={
				<>
					<EllipsisIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
						onClick={handleOpenDrawer}
					/>

					<DocumentActionsDrawerRedesign
						item={item}
						open={isDrawerOpen}
						onClose={() => setIsDrawerOpen(false)}
						activeTag={activeTag}
						isDownloaded={downloadedDocuments.documents.includes(item.filename)}
					/>
				</>
			}
		/>
	);
};
